import React, { useState, useEffect } from 'react';
import { FeedbackFormDto, FeedbackCategory } from '../types';
import { feedbackAPI, categoriesAPI } from '../services/api';
import { getQRCodeFromUrl, validatePhoneNumber } from '../utils/helpers';
import LoadingSpinner from '../components/LoadingSpinner';
import './FeedbackFormPage.css';

const FeedbackFormPage: React.FC = () => {
  const [selectedFiles, setSelectedFiles] = useState<File[]>([]);
  const [submitSuccess, setSubmitSuccess] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formErrors, setFormErrors] = useState<any>({});
  const [categories, setCategories] = useState<FeedbackCategory[]>([]);
  const [categoriesLoading, setCategoriesLoading] = useState(true);
  const [showAlert, setShowAlert] = useState(false);
  const [alertType, setAlertType] = useState<'success' | 'error'>('success');
  const [alertMessage, setAlertMessage] = useState('');
  const [isDragging, setIsDragging] = useState(false);

  const showNotification = (type: 'success' | 'error', message: string) => {
    setAlertType(type);
    setAlertMessage(message);
    setShowAlert(true);

    // Auto hide after 5 seconds
    setTimeout(() => {
      setShowAlert(false);
    }, 5000);
  };

  const qrLocation = getQRCodeFromUrl();

  // Form state
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    patientName: '',
    patientPhone: '',
    priority: 'Medium' as 'Low' | 'Medium' | 'High' | 'Critical',
    categoryId: '',
    location: qrLocation || '',
  });

  // Fetch categories
  useEffect(() => {
    const fetchCategories = async () => {
      try {
        setCategoriesLoading(true);
        const response = await categoriesAPI.getCategories();
        setCategories(response.data.filter((cat: FeedbackCategory) => cat.isActive));
      } catch (error) {
        console.error('Error fetching categories:', error);
        setCategories([]);
      } finally {
        setCategoriesLoading(false);
      }
    };

    fetchCategories();
  }, []);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));

    // Clear error when user starts typing
    if (formErrors[name]) {
      setFormErrors((prev: any) => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  const validateForm = () => {
    const errors: any = {};

    if (!formData.title.trim()) {
      errors.title = 'Konu alanı gereklidir';
    } else if (formData.title.length > 200) {
      errors.title = 'Konu en fazla 200 karakter olabilir';
    }

    if (!formData.description.trim()) {
      errors.description = 'Açıklama alanı gereklidir';
    } else if (formData.description.length > 2000) {
      errors.description = 'Açıklama en fazla 2000 karakter olabilir';
    }

    if (!formData.categoryId) {
      errors.categoryId = 'Kategori seçimi gereklidir';
    }

    if (formData.patientPhone && !validatePhoneNumber(formData.patientPhone)) {
      errors.patientPhone = 'Geçerli bir telefon numarası girin';
    }

    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (event.target.files) {
      const newFiles = Array.from(event.target.files);
      setSelectedFiles(prev => [...prev, ...newFiles]);
      event.target.value = ''; // Clear input
    }
  };

  const handleDragOver = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
  };

  const handleDragLeave = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
  };

  const handleDrop = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);

    const files = Array.from(e.dataTransfer.files);
    const validFiles = files.filter(file => {
      const isImage = file.type.startsWith('image/');
      const isPDF = file.type === 'application/pdf';
      const isDoc = file.type === 'application/msword' ||
        file.type === 'application/vnd.openxmlformats-officedocument.wordprocessingml.document';
      return isImage || isPDF || isDoc;
    });

    if (validFiles.length > 0) {
      setSelectedFiles(prev => [...prev, ...validFiles]);
    }

    if (validFiles.length < files.length) {
      showNotification('error', 'Bazı dosyalar desteklenmiyor. Sadece resim, PDF ve Word dosyaları yükleyebilirsiniz.');
    }
  };

  const removeFile = (index: number) => {
    setSelectedFiles(prev => prev.filter((_, i) => i !== index));
  };

  const handleFormSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    setIsSubmitting(true);
    setSubmitError(null);

    try {
      const formDataToSend = new FormData();

      // Add form fields
      formDataToSend.append('subject', formData.title);
      formDataToSend.append('description', formData.description);
      formDataToSend.append('priority', formData.priority);
      formDataToSend.append('categoryId', formData.categoryId);

      if (formData.patientName) {
        formDataToSend.append('patientName', formData.patientName);
      }

      if (formData.patientPhone) {
        formDataToSend.append('patientPhone', formData.patientPhone);
      }

      if (formData.location) {
        formDataToSend.append('location', formData.location);
      }

      // Add QR code source if from QR
      if (qrLocation) {
        formDataToSend.append('qrCodeSource', 'QR');
      }

      // Add files
      selectedFiles.forEach((file) => {
        formDataToSend.append('attachments', file);
      });

      const response = await feedbackAPI.createFeedback(formDataToSend);

      // API'den başarılı yanıt geldi
      showNotification('success', 'Geri bildiriminiz başarıyla gönderildi! En kısa sürede değerlendirilecektir.');

      // Reset form
      setFormData({
        title: '',
        description: '',
        patientName: '',
        patientPhone: '',
        priority: 'Medium',
        categoryId: '',
        location: qrLocation || '',
      });
      setSelectedFiles([]);
      setFormErrors({});
      setSubmitError(null);
    } catch (error: any) {
      console.error('Submission error:', error);
      const errorMessage = error.response?.data?.message || 'Geri bildiriminiz gönderilirken bir hata oluştu. Lütfen tekrar deneyiniz.';
      showNotification('error', errorMessage);
      setSubmitError(errorMessage);
    } finally {
      setIsSubmitting(false);
    }
  };

  if (categoriesLoading) {
    return <LoadingSpinner message="Form yükleniyor..." />;
  }

  return (
    <div className="container">
      {/* Alert Notification */}
      {showAlert && (
        <div className={`alert-notification ${alertType}`}>
          <div className="alert-content">
            <span className="alert-icon">
              {alertType === 'success' ? '✅' : '❌'}
            </span>
            <span className="alert-message">{alertMessage}</span>
            <button
              className="alert-close"
              onClick={() => setShowAlert(false)}
            >
              ×
            </button>
          </div>
        </div>
      )}

      <div className="feedback-form-container">
        <div className="form-card">
          <div className="form-header">
            <img src="/dicle.png" alt="Dicle Üniversitesi" className="university-logo" />
            <div className="form-header-text">
              <h1>Dicle Üniversitesi</h1>
              <p className="form-title">Hasta Geri Bildirim Formu</p>
              <span className="form-subtitle">Görüş ve önerileriniz bizim için değerlidir</span>
            </div>
          </div>

          {qrLocation && (
            <div className="alert alert-info">
              Konum: {qrLocation} - QR kod üzerinden erişildi
            </div>
          )}

          {submitError && (
            <div className="alert alert-error">
              {submitError}
            </div>
          )}

          <form onSubmit={handleFormSubmit}>
            <div className="form-section">
              <h3>İletişim Bilgileri (İsteğe bağlı)</h3>

              <div className="form-row">
                <div className="form-group">
                  <label className="form-label">Ad Soyad</label>
                  <input
                    type="text"
                    name="patientName"
                    className="form-input"
                    placeholder="İsteğe bağlı"
                    value={formData.patientName}
                    onChange={handleInputChange}
                  />
                </div>

                <div className="form-group">
                  <label className="form-label">Telefon Numarası</label>
                  <input
                    type="tel"
                    name="patientPhone"
                    className={`form-input ${formErrors.patientPhone ? 'error' : ''}`}
                    placeholder="İsteğe bağlı"
                    value={formData.patientPhone}
                    onChange={handleInputChange}
                  />
                  {formErrors.patientPhone && (
                    <div className="form-error">{formErrors.patientPhone}</div>
                  )}
                </div>
              </div>
            </div>

            <div className="form-section">
              <h3>Geri Bildirim Detayları</h3>

              <div className="form-group">
                <label className="form-label">Konu *</label>
                <input
                  type="text"
                  name="title"
                  className={`form-input ${formErrors.title ? 'error' : ''}`}
                  placeholder="Geri bildiriminizin konusunu kısaca özetleyin"
                  value={formData.title}
                  onChange={handleInputChange}
                  required
                />
                {formErrors.title && (
                  <div className="form-error">{formErrors.title}</div>
                )}
              </div>

              <div className="form-group">
                <label className="form-label">Açıklama *</label>
                <textarea
                  name="description"
                  className={`form-input ${formErrors.description ? 'error' : ''}`}
                  placeholder="Lütfen yaşadığınız sorunu veya önerinizi detaylı bir şekilde açıklayın"
                  rows={4}
                  value={formData.description}
                  onChange={handleInputChange}
                  required
                />
                {formErrors.description && (
                  <div className="form-error">{formErrors.description}</div>
                )}
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label className="form-label">Kategori *</label>
                  <select
                    name="categoryId"
                    className={`form-input ${formErrors.categoryId ? 'error' : ''}`}
                    value={formData.categoryId}
                    onChange={handleInputChange}
                    required
                  >
                    <option value="">Kategori seçin</option>
                    {categories?.map((category) => (
                      <option key={category.id} value={category.id}>
                        {category.name}
                      </option>
                    ))}
                  </select>
                  {formErrors.categoryId && (
                    <div className="form-error">{formErrors.categoryId}</div>
                  )}
                </div>

                <div className="form-group">
                  <label className="form-label">Öncelik</label>
                  <select
                    name="priority"
                    className="form-input"
                    value={formData.priority}
                    onChange={handleInputChange}
                  >
                    <option value="Low">Düşük</option>
                    <option value="Medium">Orta</option>
                    <option value="High">Yüksek</option>
                    <option value="Critical">Kritik</option>
                  </select>
                </div>
              </div>

              <div className="form-group">
                <label className="form-label">Konum</label>
                <input
                  type="text"
                  name="location"
                  className="form-input"
                  placeholder="Hastane içindeki konum (ör: 3. Kat Dahiliye Polikliniği)"
                  value={formData.location}
                  onChange={handleInputChange}
                  readOnly={!!qrLocation}
                  style={qrLocation ? { backgroundColor: '#f5f5f5', color: '#666' } : {}}
                />
                {qrLocation && (
                  <small className="form-help">
                    QR kod ile otomatik olarak seçildi
                  </small>
                )}
              </div>
            </div>

            <div className="form-section">
              <h3>Fotoğraf / Dosya Ekleme (İsteğe bağlı)</h3>

              <div
                className={`file-upload-area ${isDragging ? 'dragging' : ''}`}
                onDragOver={handleDragOver}
                onDragLeave={handleDragLeave}
                onDrop={handleDrop}
              >
                <input
                  type="file"
                  id="file-upload"
                  multiple
                  accept="image/*,.pdf,.doc,.docx"
                  onChange={handleFileSelect}
                  style={{ display: 'none' }}
                />
                <label htmlFor="file-upload" className="file-upload-btn">
                  Dosya Seç
                </label>
                <p className="drag-drop-text">
                  veya dosyaları buraya sürükleyin
                </p>
                <p className="file-types-text">
                  Desteklenen formatlar: Resim, PDF, Word
                </p>

                {selectedFiles.length > 0 && (
                  <div className="selected-files">
                    {selectedFiles.map((file, index) => (
                      <div key={index} className="file-item">
                        <span>{file.name}</span>
                        <button
                          type="button"
                          onClick={() => removeFile(index)}
                          className="remove-file-btn"
                        >
                          ✕
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>

            <button
              type="submit"
              className="btn btn-primary submit-btn"
              disabled={isSubmitting}
            >
              {isSubmitting ? 'Gönderiliyor...' : 'Geri Bildirimi Gönder'}
            </button>
          </form>
        </div>
      </div>
    </div>
  );
};

export default FeedbackFormPage;